import { Link } from "react-router-dom";
import { Cpu, FileLock2, History, KeyRound, Lock, ShieldCheck, Users } from "lucide-react";
import { LanguageSelector } from "@/components/app/LanguageSelector";
import { DashboardPreview } from "@/components/auth/DashboardPreview";

const BENEFITS = [
  [ShieldCheck, "Simplified DPDP Compliance", "Navigate India's data protection requirements without complex legal processes."],
  [Cpu, "AI-Powered Compliance", "Automate assessments, identify compliance gaps, and generate actionable recommendations."],
  [FileLock2, "Complete Data Protection Management", "Manage consent, data principals' rights, privacy policies, risks, and compliance documentation."],
];

export const Logo = ({ dark }) => (
  <Link to="/login" className="inline-flex items-center gap-2.5" data-testid="auth-logo">
    <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 shadow-lg shadow-cyan-500/20"><ShieldCheck className="h-5 w-5 text-[#0a1838]" /></span>
    <span className={`font-display text-[15px] font-bold leading-tight ${dark ? "text-white" : "text-[#0a1838]"}`}>Ainovara<span className={`block text-[11px] font-medium ${dark ? "text-slate-400" : "text-slate-500"}`}>DPDP Compliance Hub</span></span>
  </Link>
);

function BrandPanel() {
  return (
    <aside data-testid="auth-brand-panel" className="auth-brand relative hidden overflow-hidden p-10 text-white lg:flex lg:flex-col xl:p-14">
      <Logo dark />
      <div className="relative mx-auto mt-12 w-full max-w-2xl xl:mt-14">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-300/20 bg-teal-300/10 px-3 py-1 text-[11px] font-medium text-teal-200"><Lock className="h-3 w-3" />Built for India's DPDP Act, 2023</span>
        <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.1] tracking-tight xl:text-5xl" data-testid="auth-headline">Privacy Compliance. <span className="bg-gradient-to-r from-teal-300 to-cyan-300 bg-clip-text text-transparent">Simplified.</span></h1>
        <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-slate-300">Manage your DPDP Act compliance, protect personal data, and build customer trust — all from one intelligent platform.</p>
        <ul className="mt-8 space-y-4">{BENEFITS.map(([I, t, d], i) => (
          <li key={t} className="auth-in flex gap-3.5" style={{ animationDelay: `${120 + i * 90}ms` }} data-testid={`auth-benefit-${i}`}>
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-white/10 bg-white/[0.06]"><I className="h-4 w-4 text-cyan-300" /></span>
            <div><p className="text-sm font-semibold text-white">{t}</p><p className="mt-0.5 text-[13px] leading-relaxed text-slate-400">{d}</p></div>
          </li>
        ))}</ul>
      </div>
      <div className="relative mx-auto mt-10 w-full max-w-2xl"><DashboardPreview /></div>
      <p className="relative mt-auto pt-8 text-[11px] text-slate-500">Assessment platform — not a substitute for legal advice, a lawyer or a DPO.</p>
    </aside>
  );
}

function MobileBanner() {
  return (
    <div data-testid="auth-mobile-banner" className="relative overflow-hidden px-5 pb-6 pt-5 text-white lg:hidden">
      <div className="flex items-center justify-between"><Logo dark /><LanguageSelector /></div>
      <p className="mt-5 font-display text-2xl font-extrabold tracking-tight">Privacy Compliance. <span className="text-teal-300">Simplified.</span></p>
      <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-300">
        {["DPDP-ready", "AI gap analysis", "Consent & rights"].map((t) => <span key={t} className="rounded-full border border-white/10 bg-white/[0.06] px-2.5 py-1">{t}</span>)}
      </div>
    </div>
  );
}

const TRUST = [[Lock, "Encrypted in transit (HTTPS)"], [KeyRound, "Connection secrets encrypted"], [Users, "Separate data per company"], [ShieldCheck, "Role-based access"], [History, "Tamper-evident audit log"]];

function TrustBadges() {
  return (
    <ul className="mt-5 flex max-w-[460px] flex-wrap justify-center gap-2" data-testid="auth-trust-badges">
      {TRUST.map(([I, t], i) => (
        <li key={t} data-testid={`auth-trust-badge-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.06] px-2.5 py-1 text-[11px] font-medium text-slate-300 lg:border-slate-200 lg:bg-white lg:text-slate-600">
          <I className="h-3 w-3 text-teal-400 lg:text-teal-600" />{t}
        </li>
      ))}
    </ul>
  );
}

export function AuthShell({ title, subtitle, children, wide }) {
  return (
    <div className="auth-brand min-h-screen lg:grid lg:bg-none lg:bg-[#f6f8fb] lg:grid-cols-[1.35fr_1fr]">
      <BrandPanel />
      <MobileBanner />
      <main className="relative flex flex-col px-5 pb-8 sm:px-10 lg:min-h-screen lg:px-12 xl:px-16">
        <div className="hidden justify-end pt-6 lg:flex"><LanguageSelector /></div>
        <div className="flex flex-1 flex-col items-center justify-center py-8 lg:py-10">
          <div className={`auth-in w-full ${wide ? "max-w-[460px]" : "max-w-[420px]"} -mt-10 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,.04),0_12px_40px_-12px_rgba(15,23,42,.12)] sm:p-8 lg:mt-0`} data-testid="auth-card">
            <h2 className="font-display text-2xl font-bold tracking-tight text-[#0a1838]" data-testid="auth-title">{title}</h2>
            {subtitle && <p className="mt-1.5 text-sm leading-relaxed text-slate-500" data-testid="auth-subtitle">{subtitle}</p>}
            <div className="mt-6">{children}</div>
          </div>
          <TrustBadges />
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-slate-400">
          <Link to="/legal/privacy" className="hover:text-white lg:hover:text-slate-600" data-testid="footer-privacy-link">Privacy</Link>
          <Link to="/legal/terms" className="hover:text-white lg:hover:text-slate-600" data-testid="footer-terms-link">Terms</Link>
        </div>
      </main>
    </div>
  );
}
